(() => {
  const endpoint = document.currentScript?.dataset.preorderUrl;
  if (!endpoint) return;

  const rowSelector = '.upcart-public-cart-item';
  const badgeSelector = '.mot-upcart-preorder';
  let cartRoot;
  let timer;
  let revision = 0;
  let loading = false;

  const getVariantId = (row) => {
    const links = row.querySelectorAll('.upcart-public-component-product-tile__title-link[href], .upcart-public-component-product-tile__image-wrapper[href]');
    for (const link of links) {
      try {
        const id = new URL(link.getAttribute('href'), location.origin).searchParams.get('variant');
        if (id) return id;
      } catch (_) {}
    }
    return null;
  };

  let pricingData;
  const pricingSelector = '.mot-upcart-code-pricing';
  const codePriceSelector = '.upcart-public-component-product-tile__product-pricing';
  const normaliseCode = (value) => String(value || '').trim().toUpperCase();

  const renderCodePricing = () => {
    if (!pricingData || !cartRoot) return;
    const remaining = [...pricingData.items];
    const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: pricingData.currency, currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
    }).format(cents / 100);
    cartRoot.querySelectorAll(rowSelector).forEach((row) => {
      const variantId = getVariantId(row);
      const quantity = Number(row.querySelector('input[aria-label="Product quantity"]')?.value);
      const propertiesText = row.querySelector('.upcart-public-component-product-tile__properties')?.textContent || '';
      const index = remaining.findIndex((item) => String(item.variantId) === variantId && item.quantity === quantity &&
        (Array.isArray(item.properties) ? item.properties : Object.entries(item.properties || {})).filter(([key, value]) => !key.startsWith('_') && value).every(([, value]) => propertiesText.includes(String(value))));
      const item = index < 0 ? null : remaining.splice(index, 1)[0];
      const original = row.querySelector(codePriceSelector + ':not(' + pricingSelector + ')');
      let replacement = row.querySelector(pricingSelector);
      const codes = new Set((item?.codeTitles || []).map(normaliseCode));
      const tags = [...row.querySelectorAll('.upcart-public-component-product-tile__discount-codes .upcart-public-component-tag')];
      let hasCode = codes.size > 0;
      tags.forEach((tag) => {
        const isCode = codes.has(normaliseCode(tag.querySelector('.upcart-public-component-tag__text')?.textContent));
        hasCode ||= isCode;
        if (isCode) tag.setAttribute('data-mot-code-only', '');
        else tag.removeAttribute('data-mot-code-only');
      });
      const discounts = row.querySelector('.upcart-public-component-product-tile__discount-codes');
      if (hasCode && tags.every((tag) => tag.hasAttribute('data-mot-code-only'))) discounts?.setAttribute('data-mot-code-only', '');
      else discounts?.removeAttribute('data-mot-code-only');
      if (!original || !item) {
        original?.removeAttribute('data-mot-code-price');
        replacement?.remove();
        return;
      }
      const saving = Math.max(0, item.comparePrice - item.displayPrice);
      const signature = [pricingData.currency, item.displayPrice, item.comparePrice, saving].join(':');
      if (!replacement) {
        replacement = document.createElement('div');
        replacement.className = 'upcart-public-component-product-tile__product-pricing mot-upcart-code-pricing';
        original.after(replacement);
      }
      if (replacement.dataset.priceSignature !== signature) {
        const parts = [];
        const append = (className, text) => {
          const part = document.createElement('span');
          part.className = 'upcart-public-component-product-tile__' + className;
          part.textContent = text;
          parts.push(part);
        };
        if (saving > 0) append('price-compare-at', money(item.comparePrice));
        append('price', money(item.displayPrice));
        if (saving > 0) append('savings', 'Saved ' + money(saving));
        replacement.replaceChildren(...parts);
        replacement.dataset.priceSignature = signature;
      }
      original.setAttribute('data-mot-code-price', '');
    });
  };

  const summarySavings = (data) => {
    const fullPrice = Math.max(data.subtotalBeforeCoupon, data.items.reduce((sum, item) => sum + Math.max(item.comparePrice, item.displayPrice), 0));
    return {
      fullPrice,
      saleSaving: Math.max(0, fullPrice - data.subtotalBeforeCoupon),
      codeSaving: Math.max(0, data.subtotalBeforeCoupon - data.totalAfterDiscounts)
    };
  };

  const renderSummaryTotals = () => {
    if (!cartRoot || !pricingData || !Number.isFinite(pricingData.subtotalBeforeCoupon) || !Number.isFinite(pricingData.totalAfterDiscounts)) return;
    const section = cartRoot.querySelector('.styles_Footer__cartSummarySection__');
    const subtotal = section?.querySelector('.styles_Footer__cartSubtotalTitle__');
    const original = subtotal?.querySelector('.styles_Footer__cartSubtotalValue__:not(.mot-upcart-subtotal-before)');
    if (!original) return;
    const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: pricingData.currency, currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
    }).format(cents / 100);
    let before = subtotal.querySelector('.mot-upcart-subtotal-before');
    if (!before) {
      before = document.createElement('span');
      before.className = 'styles_Footer__cartSubtotalValue__ styles_Footer__alignRight__ mot-upcart-subtotal-before';
      original.after(before);
    }
    const summary = cartRoot.querySelector('.upcart-public-cart-summary');
    const { fullPrice, saleSaving, codeSaving } = summarySavings(pricingData);
    let codePill = summary?.querySelector('.mot-code-savings');
    if (summary && codeSaving > 0) {
      if (!codePill) {
        codePill = document.createElement('span');
        codePill.className = 'mot-code-savings';
        summary.append(codePill);
      }
      const savedText = 'Saved ' + money(codeSaving);
      if (codePill.textContent !== savedText) codePill.textContent = savedText;
    } else {
      codePill?.remove();
    }
    let saleRow = section.querySelector('.mot-sale-summary');
    if (saleSaving > 0) {
      if (!saleRow) {
        saleRow = document.createElement('div');
        saleRow.className = 'mot-sale-summary';
        const saleLabel = document.createElement('span');
        saleLabel.className = 'mot-sale-summary__label';
        saleLabel.textContent = 'Savings';
        const saleAmount = document.createElement('span');
        saleAmount.className = 'mot-code-savings';
        saleRow.append(saleLabel, saleAmount);
        section.append(saleRow);
      }
      const saleText = 'Saved ' + money(saleSaving);
      if (saleRow.lastElementChild.textContent !== saleText) saleRow.lastElementChild.textContent = saleText;
    } else {
      saleRow?.remove();
    }
    const beforeText = money(fullPrice);
    if (before.textContent !== beforeText) before.textContent = beforeText;
    original.setAttribute('data-mot-subtotal-original', '');
    let total = section.querySelector('.mot-upcart-summary-total');
    if (!total) {
      total = document.createElement('div');
      total.className = 'mot-upcart-summary-total';
      const label = document.createElement('span');
      label.textContent = 'Total';
      const amount = document.createElement('span');
      amount.className = 'mot-upcart-summary-total__amount';
      total.append(label, amount);
      section.append(total);
    }
    const amount = total.querySelector('.mot-upcart-summary-total__amount');
    const totalText = money(pricingData.totalAfterDiscounts);
    if (amount.textContent !== totalText) amount.textContent = totalText;
    const checkout = cartRoot.querySelector('.styles_Footer__checkoutButton__');
    if (checkout) {
      const link = checkout.querySelector('.upcart-checkout-button');
      if (link && link.getAttribute('aria-label') !== 'Checkout') link.setAttribute('aria-label', 'Checkout');
      let shipping = checkout.querySelector('.mot-checkout-shipping');
      if (!shipping) {
        shipping = document.createElement('p');
        shipping.className = 'mot-checkout-shipping';
      }
      const buttonContainer = link?.closest('.upcart-checkout-button-container');
      if (buttonContainer && buttonContainer.parentElement === checkout) {
        if (buttonContainer.nextElementSibling !== shipping) buttonContainer.after(shipping);
      } else if (checkout.lastElementChild !== shipping) {
        checkout.append(shipping);
      }
      const shippingText = 'Shipping calculated at checkout · ' + pricingData.currency;
      if (shipping.textContent !== shippingText) shipping.textContent = shippingText;
    }
  };


  const renderShipping = () => {
    if (!cartRoot) return;
    let panel = cartRoot.querySelector('.mot-upcart-shipping');
    const shipping = pricingData?.shipping;
    if (!cartRoot.querySelector(rowSelector) || !shipping || !(shipping.threshold > 0)) {
      panel?.remove();
      return;
    }
    const target = cartRoot.querySelector('.upcart-cart-body');
    if (!target) return;
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'mot-upcart-shipping';
      const pill = document.createElement('div');
      pill.className = 'mot-upcart-shipping__pill';
      const label = document.createElement('div');
      label.className = 'mot-upcart-shipping__label';
      label.setAttribute('aria-live','polite');
      const track = document.createElement('div');
      track.className = 'mot-upcart-shipping__track';
      track.setAttribute('role','progressbar');
      track.setAttribute('aria-valuemin','0');
      track.setAttribute('aria-valuemax','100');
      const fill = document.createElement('span');
      track.append(fill);
      pill.append(label,track);
      panel.append(pill);
      target.prepend(panel);
    }
    const percent = Math.max(0,Math.min(100,shipping.subtotal / shipping.threshold * 100));
    const signature = shipping.message + ':' + percent;
    if (panel.dataset.signature === signature) return;
    panel.dataset.signature = signature;
    panel.querySelector('.mot-upcart-shipping__label').textContent = shipping.message;
    const track = panel.querySelector('[role="progressbar"]');
    track.setAttribute('aria-label',shipping.message);
    track.setAttribute('aria-valuenow',String(Math.round(percent)));
    track.firstElementChild.style.width = percent + '%';
  };

  const render = (data) => {
    if (data.currency) {
      pricingData = data;
      renderCodePricing();
      renderSummaryTotals();
      renderShipping();
    }
    const statuses = new Map(data.items.map((item) => [String(item.variantId), item]));
    cartRoot?.querySelectorAll(rowSelector).forEach((row) => {
      const badge = row.querySelector(badgeSelector);
      const target = row.querySelector('.upcart-public-component-product-tile__bundle');
      const status = statuses.get(getVariantId(row));
      if (status?.preorder !== true || !target) {
        badge?.remove();
        return;
      }
      if (badge) {
        const message = badge.querySelector('.mot-upcart-preorder__dispatch');
        if (message && message.textContent !== (status.message || '')) message.textContent = status.message || '';
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.className = 'mot-upcart-preorder';
      const pill = document.createElement('span');
      pill.className = 'mot-upcart-preorder__pill';
      pill.textContent = data.label;
      wrapper.append(pill);
      const information = document.createElement('div');
      information.className = 'mot-upcart-preorder__information';
      const dispatch = document.createElement('span');
      dispatch.className = 'mot-upcart-preorder__dispatch';
      dispatch.textContent = status.message || '';
      information.append(dispatch);
      wrapper.append(information);
      target.append(wrapper);
    });
  };

  const refresh = async () => {
    if (loading || !cartRoot?.querySelector(rowSelector)) return;
    loading = true;
    const requestRevision = revision;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Pre-order status unavailable');
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const content = documentFragment.querySelector('[data-mot-upcart-preorder-data]');
      const data = JSON.parse(content?.textContent || 'null');
      if (!data || !Array.isArray(data.items) || typeof data.label !== 'string') throw new Error('Invalid pre-order status');
      if (requestRevision === revision) render(data);
    } catch (_) {
      if (requestRevision === revision) render({ items: [] });
    } finally {
      clearTimeout(timeout);
      loading = false;
      if (requestRevision !== revision) schedule();
    }
  };

  const schedule = () => {
    revision += 1;
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  };

  const isBadgeNode = (node) => {
    const element = node.nodeType === 1 ? node : node.parentElement;
    return Boolean(element?.closest(badgeSelector + ', ' + pricingSelector + ', .mot-sale-summary, .mot-code-savings, .mot-upcart-subtotal-before, .mot-upcart-summary-total, .mot-upcart-shipping'));
  };
  const cartObserver = new MutationObserver((records) => {
    const changed = records.some((record) => {
      if (record.type === 'attributes') return record.target === cartRoot || record.target.closest(rowSelector);
      if (isBadgeNode(record.target)) return false;
      const nodes = [...record.addedNodes, ...record.removedNodes];
      if (nodes.length && nodes.every(isBadgeNode)) return false;
      return record.target.closest?.(rowSelector + ', .upcart-public-cart-summary, .styles_Footer__cartSummarySection__, .upcart-checkout-button') || nodes.some((node) => node.nodeType === 1 && (node.matches(rowSelector) || node.querySelector(rowSelector)));
    });
    if (changed) schedule();
  });


  const renderTrustBadges = (root) => {
    const badges = root?.querySelector('.upcart-public-trust-badges');
    if (!badges || badges.querySelector('.mot-trust-pill')) return;
    const items = [
      ['Secure payment', '<rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V6a4 4 0 0 1 8 0v4"/>'],
      ['Fast delivery', '<path d="M3 5h11v12H3zM14 9h4l3 4v4h-7"/><circle cx="7" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>'],
      ['2K+ Reviews', '<path d="m12 3 2.8 5.8 6.4.9-4.6 4.5 1.1 6.3-5.7-3-5.7 3 1.1-6.3-4.6-4.5 6.4-.9Z"/>']
    ];
    items.forEach(([text, paths]) => {
      const pill = document.createElement('span');
      pill.className = 'mot-trust-pill';
      pill.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + paths + '</svg>';
      const label = document.createElement('span');
      label.textContent = text;
      pill.append(label);
      badges.append(pill);
    });
  };

  const connect = () => {
    const nextRoot = document.getElementById('CartPopup');
    renderTrustBadges(nextRoot);
    nextRoot?.querySelectorAll('.upcart-public-component-product-tile__savings').forEach((label) => {
      const text = label.textContent.trim();
      if (text.startsWith('(') && text.endsWith(')')) label.textContent = text.slice(1, -1);
    });
    if (nextRoot === cartRoot) {
      renderCodePricing();
      renderSummaryTotals();
      renderShipping();
      return;
    }
    cartObserver.disconnect();
    cartRoot = nextRoot;
    if (!cartRoot) return;
    cartObserver.observe(cartRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-hidden', 'href', 'value'] });
    schedule();
  };

  new MutationObserver(connect).observe(document.body, { childList: true, subtree: true, characterData: true });
  document.addEventListener('input', (event) => {
    if (event.target.closest?.('#CartPopup ' + rowSelector)) schedule();
  });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  connect();
})();


